/**
 * Agent Pages -- Deploy
 *
 * Channel overview page. Chat widget is the primary channel with full
 * management. Other channels (Email, Slack, WhatsApp, API) shown as
 * future/coming-soon cards.
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { Switch } from '../../../components/ui/switch'
import {
  MessageSquare, Mail, Hash, Smartphone, Code2,
  Settings, ArrowRight, ExternalLink, PanelLeft,
} from 'lucide-react'
import { API_BASE_URL } from '../../../utils/constants'

interface ChannelCard {
  id: string
  name: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  status: 'active' | 'coming_soon'
}

const FUTURE_CHANNELS: ChannelCard[] = [
  {
    id: 'email',
    name: 'Email',
    description: 'Connect an email address and let your agent respond to incoming messages automatically.',
    icon: Mail,
    status: 'coming_soon',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Add your agent to a Slack workspace so team members can interact with it directly.',
    icon: Hash,
    status: 'coming_soon',
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description: 'Let customers reach your agent through WhatsApp for real-time support.',
    icon: Smartphone,
    status: 'coming_soon',
  },
  {
    id: 'api',
    name: 'API',
    description: 'Integrate your agent into any application using the REST API.',
    icon: Code2,
    status: 'coming_soon',
  },
]

export const DeployPage: React.FC = () => {
  const { chatbot } = useAgentContext()
  const navigate = useNavigate()

  return (
    <div className="space-y-8">
      {/* Active channels */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Chat Widget */}
          <Card className="overflow-hidden">
            {/* Preview banner */}
            <div className="relative h-40 bg-gradient-to-br from-primary/20 via-primary/10 to-background border-b">
              {/* Mini chat mockup */}
              <div className="absolute right-6 top-6 w-56 rounded-xl border bg-card shadow-lg overflow-hidden">
                <div className="px-3 py-2 border-b bg-primary text-primary-foreground">
                  <p className="text-xs font-medium truncate">{chatbot.name}</p>
                </div>
                <div className="px-3 py-2 space-y-1.5">
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-lg px-2.5 py-1">
                      <p className="text-[10px] text-muted-foreground">Hi! How can I help you?</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-primary/10 rounded-lg px-2.5 py-1">
                      <p className="text-[10px] text-primary">I have a question...</p>
                    </div>
                  </div>
                </div>
                <div className="px-3 py-1.5 border-t">
                  <div className="rounded-md border bg-background px-2 py-1">
                    <p className="text-[9px] text-muted-foreground">Ask a question...</p>
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <MessageSquare className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">Chat Widget</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Add a floating chat window to your website. Users can ask questions and get instant answers.
                    </p>
                  </div>
                </div>
                <Switch checked={chatbot.is_active} disabled />
              </div>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/dashboard/chatbots/${chatbot.id}/deploy/widget`)}>
                  <Settings className="w-3.5 h-3.5" />
                  Manage
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => window.open(`/widget-demo?chatbot=${chatbot.id}`, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Preview
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Full-Page Chat */}
          <Card className="overflow-hidden">
            {/* Preview banner — ChatGPT-style mockup */}
            <div className="relative h-40 bg-gradient-to-br from-primary/10 via-background to-primary/5 border-b">
              <div className="absolute inset-4 flex gap-2">
                {/* Mini sidebar */}
                <div className="w-16 rounded-lg border bg-card/80 p-2 hidden sm:block">
                  <div className="h-2 w-10 rounded bg-muted mb-2" />
                  <div className="space-y-1.5">
                    <div className="h-1.5 w-full rounded bg-primary/20" />
                    <div className="h-1.5 w-10 rounded bg-muted" />
                    <div className="h-1.5 w-8 rounded bg-muted" />
                  </div>
                </div>
                {/* Mini chat area */}
                <div className="flex-1 rounded-lg border bg-card/80 p-2 flex flex-col">
                  <div className="flex-1 space-y-1.5">
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-md px-2 py-0.5">
                        <p className="text-[9px] text-muted-foreground">How can I help?</p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-primary/15 rounded-md px-2 py-0.5">
                        <p className="text-[9px] text-primary">Tell me about...</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-md px-2 py-0.5 max-w-[70%]">
                        <p className="text-[9px] text-muted-foreground">Sure! Here's what...</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-1 rounded-md border bg-background px-2 py-0.5">
                    <p className="text-[8px] text-muted-foreground">Ask anything...</p>
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <PanelLeft className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">Full-Page Chat</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      A standalone chat page with sidebar history. Share a link or embed on your site.
                    </p>
                  </div>
                </div>
                <Switch checked={chatbot.is_active} disabled />
              </div>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/dashboard/chatbots/${chatbot.id}/deploy/fullpage-chat`)}>
                  <Settings className="w-3.5 h-3.5" />
                  Manage
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => window.open(`/fullpage-chat.html?chatbotId=${chatbot.id}`, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Preview
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Future channels */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">More Channels</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {FUTURE_CHANNELS.map(channel => (
            <Card key={channel.id} className="relative opacity-75">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <channel.icon className="h-4.5 w-4.5" />
                  </div>
                  <Badge variant="outline" size="sm" className="text-muted-foreground">
                    Soon
                  </Badge>
                </div>
                <div>
                  <h3 className="text-sm font-semibold">{channel.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{channel.description}</p>
                </div>
                <Button variant="outline" size="sm" disabled className="w-full gap-1.5">
                  <ArrowRight className="w-3.5 h-3.5" />
                  Connect
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

    </div>
  )
}
